import React from "react";
import { Typography, Box } from "@mui/material";

const PrivacyPromise = () => (
    <Box sx={{ padding: '20px' }}>
        <Typography variant="h5" component="h1" gutterBottom>
            Laadu's Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
            Last Updated: August 17, 2025
        </Typography>
        <Typography variant="body1" paragraph>
            Welcome to Laadu! Our goal is to help you master any subject. To do that, we need to collect some information from you. This policy explains what we collect, how we use it, and who we share it with.
        </Typography>
        <Typography variant="body1" paragraph>
            This covers our website (quizacer.com) and mobile apps (our "Services"). By using our Services, you agree to this policy.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            What Information Do We Collect?
        </Typography>
        <Typography variant="body1" paragraph>
            We collect information in different ways to provide and improve our Services.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
            Information You Give Us
        </Typography>
        <Typography variant="body1" paragraph>
            When you sign up or use Laadu, you might provide your username, birth date, email, phone number, password, full name, profile picture, or address.
        </Typography>
        <Typography variant="body1" paragraph>
            If you sign in with Google, Facebook, or Apple, they share some basic info like your name and email. You can choose to share more to unlock features.
        </Typography>
        <Typography variant="body1" paragraph>
            When you make purchases, we collect transaction details. Our secure payment partners handle your credit card info-we don't see it.
        </Typography>
        <Typography variant="body1" paragraph>
            We also collect content you create, like study sets, images, audio, search terms, survey responses, or support messages. You can add school or course info too. If you win a contest, we collect info needed for prizes and taxes.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
            Information We Collect Automatically
        </Typography>
        <Typography variant="body1" paragraph>
            When you use Laadu, we automatically collect info about your activity: device type, IP address, study time, pages visited, and how you interact with features. We use cookies, pixels, and scripts to understand your usage and improve Laadu.
        </Typography>
        <Typography variant="body1" paragraph>
            Social media buttons may collect your IP address-these are provided by the social media companies.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
            Information From Other Sources
        </Typography>
        <Typography variant="body1" component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Partners:</strong> We may get info from companies we work with to improve services and keep data accurate.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Advertising Companies:</strong> They may use cookies to collect info for more relevant ads.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Other Users:</strong> If a friend invites you, we may get your name and email from them.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Public Sources:</strong> We might collect info from public social media posts or forums.
                </Typography>
            </li>
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            How We Use Your Information
        </Typography>
        <Typography variant="body1" component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
            <li>
                <Typography variant="body1" component="div">
                    <strong>To run Laadu:</strong> Log you in, host study sets, process payments, send updates, and suggest relevant content.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>To improve:</strong> Analyze usage, conduct research, and gather feedback.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Customer support:</strong> Answer your questions and provide help.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Marketing:</strong> Send emails about features or offers, and show relevant ads.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Safety:</strong> Prevent fraud, abuse, and illegal activities.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Enforce rules:</strong> Uphold our Terms of Service and policies.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Verify identity:</strong> Confirm who you are when needed.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Legal compliance:</strong> Respond to laws and legal requests.
                </Typography>
            </li>
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            Who We Share Your Information With
        </Typography>
        <Typography variant="body1" paragraph>
            Some info is public: your profile, classes, and study sets may be visible to others.
        </Typography>
        <Typography variant="body1" component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Service providers:</strong> Companies helping us run Laadu (payment processors, hosting, analytics) must follow our security rules.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Legal reasons:</strong> If required by law, to prevent harm, or protect rights and safety.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Company changes:</strong> If Laadu is sold or acquired, your info may transfer-we'll notify you.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>With permission:</strong> When you consent to sharing.
                </Typography>
            </li>
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            Your Choices and Controls
        </Typography>
        <Typography variant="body1" component="ul" sx={{ listStyleType: 'disc', pl: 4 }}>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Emails:</strong> Opt out of marketing through settings or unsubscribe links (essential messages continue).
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Cookies:</strong> Manage preferences via website footer or browser settings.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Online ads:</strong> Opt out through Digital Advertising Alliance tools.
                </Typography>
            </li>
            <li>
                <Typography variant="body1" component="div">
                    <strong>Your content:</strong> Choose public/private study sets, edit or delete content, or delete your entire account.
                </Typography>
            </li>
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            "Do Not Track" Signals
        </Typography>
        <Typography variant="body1" paragraph>
            We don't currently respond to "Do Not Track" signals but recognize other privacy controls where required by law.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            How Long We Keep Your Information
        </Typography>
        <Typography variant="body1" paragraph>
            We keep info while your account is active or for legal/business needs. After that, we delete it but may keep anonymized data indefinitely.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            How We Protect Your Information
        </Typography>
        <Typography variant="body1" paragraph>
            We use reasonable security measures, but no online service is 100% secure. Contact laaduprivacy@yahoo.com with security questions.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            Links to Other Websites
        </Typography>
        <Typography variant="body1" paragraph>
            We link to sites we don't control. We're not responsible for their privacy practices-please review their policies.
        </Typography>
      
        <Typography variant="h6" component="h2" gutterBottom>
            Your Rights by Location
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
            Europe (EEA, UK, Switzerland)
        </Typography>
        <Typography variant="body1" paragraph>
            GDPR gives you rights to access, correct, delete, or restrict your data. Email laaduprivacy@yahoo.com with "GDPR Request." We use approved frameworks for data transfers to the U.S.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
            California
        </Typography>
        <Typography variant="body1" paragraph>
            CCPA rights include knowing what data we collect, requesting deletion, and opting out of "sales" for advertising. Use the footer link or email laaduprivacy@yahoo.com with "CCPA Request." We don't sell data of users under 16.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
            Other U.S. States
        </Typography>
        <Typography variant="body1" paragraph>
            State laws may give similar rights. Email laaduprivacy@yahoo.com with "Privacy Request."
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
            We may update this policy. Significant changes will be notified before taking effect. Continued use means you accept updates.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
            Have Questions?
        </Typography>
        <Typography variant="body1" paragraph>
            Contact our privacy team at:
        </Typography>
        <Typography variant="body1" paragraph>
            Laadu, Inc.<br />
            Attention: Privacy Team<br />
            laaduprivacy@yahoo.com
        </Typography>
    </Box>
);

export default PrivacyPromise;